import React, { useEffect, useState } from 'react'
import { Button, Col, Row, Space, Switch, Tooltip } from 'antd'
import { DetailDrawer } from 'Components/common/MainLayout'
import { PrimaryButton } from 'Components/button'
import styled from 'styled-components'
import { ProductOptions, CreateReq, MultiUom } from 'Store/product/types'
import { AppInput, LabelItem, UploadForm } from 'Components/form'
import { cloneDeep } from 'lodash'
import {
  formValidation,
  isASCII,
  isFormDisabled,
  isFormError,
  isFormRequired,
  roundNumber,
} from 'helpers/format'
import ChoosePhotosModal from './ChoosePhotosModal'
import { InfoCircleOutlined } from '@ant-design/icons'
import RootState from 'typings/RootState'
import { selectCurrentUser } from 'Store/user/selector'
import { connect } from 'react-redux'
import { DefaultStatus } from 'helpers/constanst'

type Props = {
  productInfo?: CreateReq
  data?: ProductOptions
  isShow: boolean
  onClose: () => void
  onSave: (data: ProductOptions) => void
  token?: string
  onAddProductImage?: (file: string[]) => void
  disableInput?: boolean
  uom: string
} & ReduxProps

const ProductOptionDrawer = (props: Props) => {
  const {
    data,
    isShow,
    onClose,
    onSave,
    productInfo,
    token,
    onAddProductImage = () => {},
    disableInput = false,
    currentUser,
    uom,
  } = props

  const isFranchise = currentUser?.activeMerchant?.is_fc === true
  const requireFeild = ['title', 'quantity', 'price', 'weight']
  const disableFirstUpdate = ['title', 'detail', 'width', 'height', 'depth', 'length', 'area', 'photo']
  const disableFranchise = [
    'title',
    'detail',
    'width',
    'height',
    'depth',
    'length',
    'area',
    'photo',
    'weight',
  ]
  const initForm = {
    title: '',
    area: 0,
    barcode: '',
    depth: 0,
    discount: 0,
    height: 0,
    length: 0,
    photo: '',
    price: 0,
    publish: true,
    quantity: 0,
    total: 0,
    total_amount: 0,
    total_discount: 0,
    weight: 0,
    width: 0,
  } as ProductOptions
  const [formData, setFormData] = useState(cloneDeep(initForm))
  const [formError, setFormError] = useState([] as string[])
  const [chooseImage, setChooseImage] = useState(false)
  const title = formData?.id ? 'แก้ไขตัวเลือกสินค้า' : 'เพิ่มตัวเลือกสินค้าใหม่'

  const validateForm = () => {
    const error = formValidation(requireFeild, formData)
    if (formData.barcode !== '') {
      const isAscii = isASCII(formData.barcode)
      if (!isAscii) {
        error.push('barcode_format')
      }
    }

    setFormError([...error])
    return error.length > 0 ? false : true
  }

  useEffect(() => {
    if (isShow && data) {
      const form = {
        ...data,
      } as ProductOptions
      setFormData({ ...form })
    }
    return () => {
      setFormData(cloneDeep(initForm))
      setFormError([])
    }
  }, [isShow, data])

  useEffect(() => {
    if (formError.length > 0) {
      validateForm()
    }
  }, [formData])

  const isRequired = (name: string) => {
    return isFormRequired(requireFeild, name)
  }

  const isError = (name: string) => {
    return isFormError(formError, name)
  }

  const isDisabled = (name: string) => {
    return (
      (disableInput && isFormDisabled(isFranchise ? disableFranchise : disableFirstUpdate, name)) || false
    )
  }

  const decimalCount = (num: number) => {
    // Convert to String
    const numStr = String(num)
    // String Contains Decimal
    if (numStr.includes('.')) {
      return numStr.split('.')[1].length
    }
    // String Does Not Contain Decimal
    return 0
  }

  const onUpdateData = (val: any, key: string) => {
    if (key === 'price') {
      const count = decimalCount(val)
      if (count > 2) return
    }

    setFormData({
      ...formData,
      [key]: val,
    })
  }

  const getMultiUoms = () => {
    const findMaster = data?.uoms?.filter(item => item.is_master)
    const master = {
      barcode: formData?.barcode,
      multiplier: 1,
      offline_price: 0,
      price: formData?.price || 0,
      price_a: 0,
      price_b: 0,
      price_c: 0,
      purchase_price: 0,
      status: DefaultStatus.ACTIVE,
      uom_name: uom,
      id: (findMaster && findMaster[0].id) || 0,
      is_master: true,
      is_default: true,
    } as MultiUom
    return [master]
  }

  const onClickSave = (): void => {
    if (!validateForm()) return
    formData.uoms = getMultiUoms()
    onSave(formData)
  }

  const openModalChooseImage = () => {
    setChooseImage(true)
  }

  const closeModalChooseImage = () => {
    setChooseImage(false)
  }

  const onClickChooseImage = (): void => {
    openModalChooseImage()
  }

  const onConfirmChooseImage = (url: string): void => {
    onUpdateData(url, 'photo')
    setChooseImage(false)
  }

  const onUploadNewImage = (file: string[]): void => {
    onAddProductImage(file)
  }

  const footer = (
    <Space size="middle" align="baseline">
      <PrimaryButton text="ยกเลิก" btnType="Cancel" onClick={onClose} />
      <PrimaryButton text="บันทึก" onClick={onClickSave} />
    </Space>
  )

  return (
    <DetailDrawer title={title} width={'50%'} onCloseDrawer={onClose} visible={isShow} footer={footer}>
      <ChoosePhotosModal
        isShow={chooseImage}
        onClose={closeModalChooseImage}
        onSelectFile={onConfirmChooseImage}
        onUpdateData={onUploadNewImage}
        productPhotos={productInfo?.photos || []}
        token={token}
        key="ChoosePhotosModal"
      />
      <Row align="top">
        {!formData.photo && (
          <Col md={24} xs={24}>
            <LabelItem label={`รูปภาพสินค้า`} /> <br />
            <AddImage type="dashed" onClick={onClickChooseImage} disabled={isDisabled('photo')}>
              เลือกรูปภาพ
            </AddImage>
          </Col>
        )}
        {formData.photo && (
          <Col md={24} xs={24}>
            <UploadForm
              token={''}
              onUpdateData={onUpdateData}
              currentFile={formData.photo}
              folder="photo"
              listType="picture-card"
              isPrivate={true}
              acceptType="image/png,image/jpeg"
              withCropImage={true}
              aspect={2.59898477157}
              name="photo"
              description=""
              isRequired={isRequired('photo')}
              isError={isError('photo')}
              label="รูปภาพสินค้า"
              key="photo-product-options"
              readOnly={isDisabled('photo')}
            />
          </Col>
        )}
      </Row>
      <Row justify="start" gutter={16}>
        <Col md={12} xs={24}>
          <AppInput
            name="title"
            label="ชื่อ"
            isRequired={isRequired('title')}
            isError={isError('title')}
            value={formData.title}
            onChange={onUpdateData}
            resProps={{ disabled: isDisabled('title') }}
          />
        </Col>
        <Col md={12} xs={24}>
          <AppInput
            name="barcode"
            label="​Barcode"
            isRequired={isRequired('barcode')}
            specialRequired={formData.barcode !== ''}
            isError={isError('barcode') || isError('barcode_format')}
            errorMsg={isError('barcode_format') ? 'รูปแบบ Barcode ให้ถูกต้อง' : 'กรุณาระบุ Barcode'}
            value={formData.barcode}
            onChange={onUpdateData}
            resProps={{ disabled: isDisabled('barcode') }}
            labelIcon={
              <Tooltip title="Barcode รองรับชุดตัวอักษร 128 ASCII (ไม่รองรับภาษาไทย)">
                &nbsp; <InfoCircleOutlined />
              </Tooltip>
            }
          />
        </Col>
        <Col md={12} xs={24}>
          <AppInput
            name="quantity"
            label="สต๊อก"
            type="number"
            isRequired={isRequired('quantity')}
            isError={isError('quantity')}
            value={formData.quantity}
            onChange={onUpdateData}
            resProps={{ disabled: isDisabled('quantity') }}
          />
        </Col>
        <Col md={12} xs={24}>
          <AppInput
            name="price"
            label="ราคา"
            type="number"
            isRequired={isRequired('price')}
            isError={isError('price')}
            value={formData.price}
            onChange={onUpdateData}
            resProps={{ disabled: isDisabled('price') }}
          />
        </Col>
        <Col md={12} xs={24}>
          <AppInput
            name="weight"
            label="น้ำหนัก"
            type="number"
            isRequired={isRequired('weight')}
            isError={isError('weight')}
            value={roundNumber(formData.weight)}
            onChange={onUpdateData}
            resProps={{ disabled: isDisabled('weight'), suffix: 'กก.' }}
          />
        </Col>
        <Col md={12} xs={24}>
          <AppInput
            name="width"
            label="ความกว้าง"
            type="number"
            isRequired={isRequired('width')}
            isError={isError('width')}
            value={roundNumber(formData.width)}
            onChange={onUpdateData}
            resProps={{ disabled: isDisabled('width'), suffix: 'ซม.' }}
          />
        </Col>
        <Col md={12} xs={24}>
          <AppInput
            name="height"
            label="ความสูง"
            type="number"
            isRequired={isRequired('height')}
            isError={isError('height')}
            value={roundNumber(formData.height)}
            onChange={onUpdateData}
            resProps={{ disabled: isDisabled('height'), suffix: 'ซม.' }}
          />
        </Col>
        <Col md={12} xs={24}>
          <AppInput
            name="depth"
            label="ความลึก"
            type="number"
            isRequired={isRequired('depth')}
            isError={isError('depth')}
            value={roundNumber(formData.depth)}
            onChange={onUpdateData}
            resProps={{ disabled: isDisabled('depth'), suffix: 'ซม.' }}
          />
        </Col>
        <Col md={12} xs={24}>
          <AppInput
            name="length"
            label="ความยาว"
            type="number"
            isRequired={isRequired('length')}
            isError={isError('length')}
            value={roundNumber(formData.length)}
            onChange={onUpdateData}
            resProps={{ disabled: isDisabled('length'), suffix: 'ซม.' }}
          />
        </Col>
        <Col md={12} xs={24}>
          <AppInput
            name="area"
            label="พื้นที่"
            type="number"
            isRequired={isRequired('area')}
            isError={isError('area')}
            value={roundNumber(formData.area)}
            onChange={onUpdateData}
            resProps={{ disabled: isDisabled('area'), suffix: 'ตรม.' }}
          />
        </Col>
        <Col md={24} xs={24}>
          <SwitchStyled>
            <Switch
              checked={formData.publish}
              onChange={(val: boolean) => onUpdateData(val, 'publish')}
              disabled={isDisabled('status')}
            />
            &nbsp; &nbsp;
            <LabelItem label={`สถานะสินค้า : ${formData.publish ? 'เปิดใช้งาน' : 'ปิดใช้งาน'}`} />
          </SwitchStyled>
        </Col>
      </Row>
    </DetailDrawer>
  )
}

const SwitchStyled = styled.div`
  margin: 10px 0;
`
const AddImage = styled(Button)`
  margin-top: 5px;
  margin-bottom: 20px;
  width: 102px;
  height: 102px;
  font-size: 12px;
`
const mapStateToProps = (state: RootState) => {
  return {
    currentUser: selectCurrentUser(state),
  }
}

type ReduxProps = ReturnType<typeof mapStateToProps>

export default connect(mapStateToProps)(ProductOptionDrawer)
